import { useEffect, useRef } from 'react';

import type { DictationStatusType } from '@/lib/dictation/types';

interface UseDictationSoundEffectsOptions {
  status: DictationStatusType | undefined;
  /**
   * True while `getUserMedia` is awaiting the browser's permission prompt —
   * comes from `useMicrophonePermission`. Treated as the first edge of
   * activation so the start sound fires on click even before the adapter
   * reports `'starting'`.
   */
  isRequestingMicPermission: boolean;
  /** Brief "click heard" sound, fired the instant the user activates the button. */
  playStartSound: () => void;
  /** "Now listening" confirmation, fired when the adapter reaches `running`. */
  playConnectedSound: () => void;
  /** End-of-session sound, fired when leaving the active (`starting`/`running`) state. */
  playStopSound: () => void;
}

/**
 * Plays start / connected / stop chimes on dictation lifecycle transitions.
 *
 * Three audible edges:
 *   • **start**  — on the edge into activation (permission-requesting OR the
 *     adapter entering `'starting'`). Fires at click time, before the adapter
 *     is ready.
 *   • **connected** — on the edge into `status === 'running'`. Distinguishes
 *     "connecting" (silent after the start click) from "actually listening".
 *   • **stop** — on the edge out of actually-active (`'starting'`/`'running'`).
 *     Permission-denied flips activation off without ever reaching active,
 *     so no stop sound plays — nothing dictated, nothing to stop.
 */
export function useDictationSoundEffects({
  status,
  isRequestingMicPermission,
  playStartSound,
  playConnectedSound,
  playStopSound,
}: UseDictationSoundEffectsOptions) {
  const wasActivatingRef = useRef(false);
  const wasRunningRef = useRef(false);
  const wasActiveRef = useRef(false);

  useEffect(() => {
    const isRunning = status === 'running';
    const isActive = status === 'starting' || isRunning;
    const isActivating = isActive || isRequestingMicPermission;

    if (isActivating && !wasActivatingRef.current) {
      playStartSound();
    }

    if (isRunning && !wasRunningRef.current) {
      playConnectedSound();
    }

    if (!isActive && wasActiveRef.current) {
      playStopSound();
    }

    wasActivatingRef.current = isActivating;
    wasRunningRef.current = isRunning;
    wasActiveRef.current = isActive;
  }, [status, isRequestingMicPermission, playStartSound, playConnectedSound, playStopSound]);
}
